// Section library + editor field schemas for the venue page builder

export const SECTION_LIBRARY = [
  { type: 'text_block', label: 'Text block', desc: 'Heading + paragraphs, left or centered' },
  { type: 'text_image', label: 'Text + Image', desc: 'Copy beside a photo, image left or right' },
  { type: 'card_grid', label: 'Card grid (image + text + CTA)', desc: 'Cards like Rooms & villas — photo, title, price, text, button' },
  { type: 'image_banner', label: 'Image banner', desc: 'Full-width photo with caption' },
  { type: 'gallery_grid', label: 'Gallery grid', desc: 'Extra image grid, 2–4 columns' },
  { type: 'video', label: 'Video', desc: 'YouTube video with heading' },
  { type: 'video_strip', label: 'Video strip (YouTube)', desc: 'Horizontal scroll strip of YouTube videos — unlimited, tap to play' },
  { type: 'quote', label: 'Quote / highlight', desc: 'Large pull-quote with attribution' },
  { type: 'cta_banner', label: 'CTA banner', desc: 'Heading + button, dark or light' },
  { type: 'stats_row', label: 'Stats row', desc: 'Big numbers with labels' },
  { type: 'feature_checklist', label: 'Feature checklist', desc: 'Bulleted list in columns' },
  { type: 'faq_accordion', label: 'FAQ accordion', desc: 'Additional Q&A accordion' },
  { type: 'map_embed', label: 'Map embed', desc: 'Google map for any address' },
  { type: 'availability_calendar', label: 'Availability calendar', desc: 'Mark booked dates, guests pick dates & see a budget estimate' },
  { type: 'cost_calculator', label: 'Cost Calculator (Pricing Engine)', desc: 'Full wedding cost calculator with live estimate, PDF & lead capture' },
  { type: 'decor_calculator', label: 'Decor calculator', desc: 'Base decor package + add-on services with quantities — live estimate & PDF download' },
  { type: 'meet_team', label: 'Meet the team', desc: 'Portraits of the crew who run your event — unlimited members' },
  { type: 'wedding_album', label: 'Wedding album', desc: 'Category-wise photo album with chapter tabs' },
  { type: 'celebrations_hosted', label: 'Celebrations hosted', desc: 'Title + short description grid — unlimited items' },
  { type: 'custom_html', label: 'Custom HTML / embed', desc: 'Paste any widget or embed code' },
  { type: 'divider', label: 'Divider / spacer', desc: 'A thin line or breathing space' }
];

export const sectionLabel = (type) => SECTION_LIBRARY.find(s => s.type === type)?.label || type;

export const emptySectionData = (type) => JSON.parse(JSON.stringify({
  text_block: { kicker: '', heading: '', body: '', align: 'left' },
  text_image: { kicker: '', heading: '', body: '', image: '', image_side: 'right', cta_label: '', cta_url: '' },
  card_grid: { kicker: '', heading: '', intro: '', columns: '3', cards: [{ image: '', title: '', price: '', text: '', cta_label: '', cta_url: '' }] },
  image_banner: { image: '', heading: '', caption: '', height: 'medium' },
  gallery_grid: { kicker: '', heading: '', columns: '3', photos: [] },
  video: { kicker: '', heading: '', video_url: '', caption: '' },
  video_strip: { kicker: '', heading: '', intro: '', videos: [] },
  quote: { quote: '', attribution: '' },
  cta_banner: { heading: '', copy: '', cta_label: 'Talk to WVC Team', cta_url: '', style: 'dark' },
  stats_row: { stats: [{ value: '', label: '' }] },
  feature_checklist: { kicker: '', heading: '', columns: '2', items: [] },
  faq_accordion: { kicker: 'Questions Every Couple Asks', heading: '', items: [{ q: '', a: '' }] },
  map_embed: { kicker: '', heading: '', address: '', note: '' },
  availability_calendar: { kicker: 'Availability', title: 'Choose your dates', intro: '' },
  cost_calculator: {},
  decor_calculator: {
    question: 'Can the décor feel as personal as our celebration?', kicker: 'Decoration Packages & Add Ons',
    title: 'Know Your Décor Budget Before You Decide', intro: '',
    base_name: 'Base decor package', base_price: 0, base_inclusions: [], addons: [], disclaimer: ''
  },
  meet_team: { kicker: 'The People Behind Your Wedding', question: '', intro: '', members: [{ name: '', role: '', description: '', photo_url: '' }] },
  wedding_album: { kicker: '', heading: '', note: '', chapters: [{ name: 'Wedding', photos: [] }] },
  celebrations_hosted: { kicker: '', heading: '', intro: '', items: [{ title: '', description: '' }] },
  custom_html: { html: '' },
  divider: { style: 'line', size: 'medium' }
}[type] || {}));

// Field inputs: text | textarea | number | select | lines (textarea -> array) | image | objlist
export const EDITOR_SCHEMAS = {
  text_block: [
    { key: 'kicker', label: 'Kicker (small gold label)', input: 'text' },
    { key: 'heading', label: 'Heading', input: 'text' },
    { key: 'body', label: 'Body text', input: 'textarea', rows: 5 },
    { key: 'align', label: 'Alignment', input: 'select', options: [['left', 'Left'], ['center', 'Centered']] }
  ],
  text_image: [
    { key: 'kicker', label: 'Kicker', input: 'text' },
    { key: 'heading', label: 'Heading', input: 'text' },
    { key: 'body', label: 'Body text', input: 'textarea', rows: 4 },
    { key: 'image', label: 'Image', input: 'image' },
    { key: 'image_side', label: 'Image side', input: 'select', options: [['right', 'Right'], ['left', 'Left']] },
    { key: 'cta_label', label: 'Button label (optional)', input: 'text' },
    { key: 'cta_url', label: 'Button link (URL or /path)', input: 'text' }
  ],
  card_grid: [
    { key: 'kicker', label: 'Kicker', input: 'text' },
    { key: 'heading', label: 'Heading', input: 'text' },
    { key: 'intro', label: 'Intro', input: 'textarea', rows: 2 },
    { key: 'columns', label: 'Columns', input: 'select', options: [['2', '2'], ['3', '3'], ['4', '4']] },
    {
      key: 'cards', label: 'Cards', input: 'objlist', itemLabel: 'Card', fields: [
        { key: 'image', label: 'Image', input: 'image' },
        { key: 'title', label: 'Title', input: 'text' },
        { key: 'price', label: 'Price label (e.g. ₹12,000 / night)', input: 'text' },
        { key: 'text', label: 'Text', input: 'textarea', rows: 2 },
        { key: 'cta_label', label: 'Button label', input: 'text' },
        { key: 'cta_url', label: 'Button link', input: 'text' }
      ]
    }
  ],
  image_banner: [
    { key: 'image', label: 'Image', input: 'image' },
    { key: 'heading', label: 'Heading (overlaid)', input: 'text' },
    { key: 'caption', label: 'Caption', input: 'text' },
    { key: 'height', label: 'Height', input: 'select', options: [['medium', 'Medium'], ['tall', 'Tall']] }
  ],
  gallery_grid: [
    { key: 'kicker', label: 'Kicker', input: 'text' },
    { key: 'heading', label: 'Heading', input: 'text' },
    { key: 'columns', label: 'Columns', input: 'select', options: [['2', '2'], ['3', '3'], ['4', '4']] },
    { key: 'photos', label: 'Photo URLs (one per line)', input: 'lines', rows: 5 }
  ],
  video: [
    { key: 'kicker', label: 'Kicker', input: 'text' },
    { key: 'heading', label: 'Heading', input: 'text' },
    { key: 'video_url', label: 'YouTube video URL', input: 'text' },
    { key: 'caption', label: 'Caption / description', input: 'textarea', rows: 2 }
  ],
  video_strip: [
    { key: 'kicker', label: 'Kicker', input: 'text' },
    { key: 'heading', label: 'Heading', input: 'text' },
    { key: 'intro', label: 'Intro', input: 'textarea', rows: 2 },
    { key: 'videos', label: 'YouTube URLs or IDs (one per line)', input: 'lines', rows: 5 }
  ],
  quote: [
    { key: 'quote', label: 'Quote', input: 'textarea', rows: 3 },
    { key: 'attribution', label: 'Attribution (optional)', input: 'text' }
  ],
  cta_banner: [
    { key: 'heading', label: 'Heading', input: 'text' },
    { key: 'copy', label: 'Supporting copy', input: 'textarea', rows: 2 },
    { key: 'cta_label', label: 'Button label', input: 'text' },
    { key: 'cta_url', label: 'Button link (leave empty to open enquiry)', input: 'text' },
    { key: 'style', label: 'Style', input: 'select', options: [['dark', 'Dark green'], ['light', 'Light']] }
  ],
  stats_row: [
    {
      key: 'stats', label: 'Stats', input: 'objlist', itemLabel: 'Stat', fields: [
        { key: 'value', label: 'Value (e.g. 95)', input: 'text' },
        { key: 'label', label: 'Label (e.g. Weddings Hosted)', input: 'text' }
      ]
    }
  ],
  feature_checklist: [
    { key: 'kicker', label: 'Kicker', input: 'text' },
    { key: 'heading', label: 'Heading', input: 'text' },
    { key: 'columns', label: 'Columns', input: 'select', options: [['1', '1'], ['2', '2'], ['3', '3']] },
    { key: 'items', label: 'Checklist items (one per line)', input: 'lines', rows: 6 }
  ],
  faq_accordion: [
    { key: 'kicker', label: 'Kicker', input: 'text' },
    { key: 'heading', label: 'Heading', input: 'text' },
    {
      key: 'items', label: 'Questions', input: 'objlist', itemLabel: 'FAQ', fields: [
        { key: 'q', label: 'Question', input: 'text' },
        { key: 'a', label: 'Answer', input: 'textarea', rows: 3 }
      ]
    }
  ],
  map_embed: [
    { key: 'kicker', label: 'Kicker', input: 'text' },
    { key: 'heading', label: 'Heading', input: 'text' },
    { key: 'address', label: 'Address or place name', input: 'text' },
    { key: 'note', label: 'Note under map', input: 'text' }
  ],
  meet_team: [
    { key: 'kicker', label: 'Kicker', input: 'text' },
    { key: 'question', label: 'Heading / question', input: 'text' },
    { key: 'intro', label: 'Intro', input: 'textarea', rows: 3 },
    {
      key: 'members', label: 'Team members', input: 'objlist', itemLabel: 'Member', fields: [
        { key: 'name', label: 'Name', input: 'text' },
        { key: 'role', label: 'Role', input: 'text' },
        { key: 'photo_url', label: 'Photo', input: 'image' },
        { key: 'description', label: 'Description', input: 'textarea', rows: 2 }
      ]
    }
  ],
  wedding_album: [
    { key: 'kicker', label: 'Kicker', input: 'text' },
    { key: 'heading', label: 'Heading', input: 'text' },
    { key: 'note', label: 'Note', input: 'text' },
    {
      key: 'chapters', label: 'Chapters', input: 'objlist', itemLabel: 'Chapter', fields: [
        { key: 'name', label: 'Chapter name (e.g. Haldi)', input: 'text' },
        { key: 'photos', label: 'Photo URLs (one per line)', input: 'lines', rows: 4 }
      ]
    }
  ],
  celebrations_hosted: [
    { key: 'kicker', label: 'Kicker', input: 'text' },
    { key: 'heading', label: 'Heading', input: 'text' },
    { key: 'intro', label: 'Intro', input: 'textarea', rows: 2 },
    {
      key: 'items', label: 'Items', input: 'objlist', itemLabel: 'Item', fields: [
        { key: 'title', label: 'Title', input: 'text' },
        { key: 'description', label: 'Short description', input: 'textarea', rows: 2 }
      ]
    }
  ],
  custom_html: [
    { key: 'html', label: 'HTML / embed code', input: 'textarea', rows: 8 }
  ],
  divider: [
    { key: 'style', label: 'Style', input: 'select', options: [['line', 'Thin line'], ['space', 'Empty space']] },
    { key: 'size', label: 'Size', input: 'select', options: [['small', 'Small'], ['medium', 'Medium'], ['large', 'Large']] }
  ],
  decor_calculator: [
    { key: 'question', label: 'Question (quote above section)', input: 'text' },
    { key: 'kicker', label: 'Kicker', input: 'text' },
    { key: 'title', label: 'Title', input: 'text' },
    { key: 'intro', label: 'Intro', input: 'textarea', rows: 2 },
    { key: 'base_name', label: 'Base package name', input: 'text' },
    { key: 'base_price', label: 'Base package price (₹)', input: 'number' },
    { key: 'base_inclusions', label: 'Base package inclusions (one per line)', input: 'lines', rows: 6 },
    {
      key: 'addons', label: 'Add-on services', input: 'objlist', itemLabel: 'Add-on', fields: [
        { key: 'name', label: 'Name', input: 'text' },
        { key: 'price', label: 'Price (₹)', input: 'number' },
        { key: 'unit', label: 'Unit (e.g. Per Session)', input: 'text' },
        { key: 'note', label: 'Note', input: 'text' }
      ]
    },
    { key: 'disclaimer', label: 'Disclaimer', input: 'textarea', rows: 3 }
  ]
};

// Derive a display title for a section row in the admin list
export const sectionRowTitle = (section) => {
  const d = section.data || {};
  return d.heading || d.title || d.question || d.quote || sectionLabel(section.type);
};

export const newSection = (type) => ({
  id: `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
  type,
  visible: true,
  data: emptySectionData(type)
});
